import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import API from "../api";
import "./ScheduleHighlightsPage.css"; // make sure this points to your CSS file

export default function ScheduleHighlightsPage() {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!user?.leagueid) {
        setError("No league assigned.");
        setLoading(false);
        return;
      }

      try {
        const res = await API.get(`/league/schedule/${user.leagueid}/schedule-grid`);
        setSchedule(res.data);
      } catch (err) {
        setError("Failed to load schedule");
        console.error("Schedule load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [user?.leagueid]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
  };

  const formatDate = (isoDateStr) => {
    const date = new Date(isoDateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { month: "numeric", day: "2-digit" });
  };

  const getOpponent = (teamId, weekNumber, matchups) => {
    const match = matchups.find(
      (m) => m.week_number === weekNumber && (m.team1 === teamId || m.team2 === teamId)
    );
    if (!match) return "";
    return match.is_bye ? "BYE" : match.team1 === teamId ? match.team2 : match.team1;
  };

  const isSpecialWeek = (week) =>
    week.type?.toLowerCase() === "fun round" || week.type?.toLowerCase() === "position round";

  if (loading) return <Spinner animation="border" className="m-5" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!schedule) return null;
  if (user?.role !== 3) return <Alert variant="danger">Access denied: Admins only</Alert>;

  const { teams, weeks, matchups, teamDetails } = schedule;

  return (
    <div className="printable-area">
      <div className="text-end mb-2 no-print">
        <button className="btn btn-sm btn-outline-primary" onClick={() => window.print()}>
          Print All Pages
        </button>
      </div>

      <h4 className="text-center mb-3">Printable Team Highlight Schedules</h4>

      {teams.map((teamId, idx) => (
        <div key={idx} className="page-break">
          <Table bordered className="text-center schedule-table">
            <thead>
              <tr>
                <th>Date</th>
                {teams.map((t) => (
                  <th key={t} className={t === teamId ? "highlight-column" : ""}>
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) =>
                isSpecialWeek(w) ? (
                  <tr key={w.week_number} className="special-week">
                    <td>{formatDate(w.date)}</td>
                    <td colSpan={teams.length}>
                      {w.type?.toLowerCase() === "fun round" && "🎉 Fun Round"}
                      {w.type?.toLowerCase() === "position round" && "Position Round"}
                    </td>
                  </tr>
                ) : (
                  <tr key={w.week_number}>
                    <td>{formatDate(w.date)}</td>
                    {teams.map((t) => (
                      <td key={t} className={t === teamId ? "highlight-column" : ""}>
                        {getOpponent(t, w.week_number, matchups)}
                      </td>
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </Table>

          <Table bordered className="text-center roster-table">
            <tbody>
              {Array.from({ length: Math.ceil(teamDetails.length / 2) }).map((_, i) => {
                const leftTeam = teamDetails[i];
                const rightTeam = teamDetails[i + 10];
                return (
                  <React.Fragment key={i}>
                    <tr>
                      <td rowSpan={2}>{leftTeam?.id || ""}</td>
                      <td>
                        {leftTeam?.player1 ? `${leftTeam.player1.firstname} ${leftTeam.player1.lastname}` : ""}
                      </td>
                      <td>{formatPhoneNumber(leftTeam?.player1?.phone) || ""}</td>
                      <td rowSpan={2} className="divider-column">&nbsp;</td>
                      <td rowSpan={2}>{rightTeam?.id || ""}</td>
                      <td>
                        {rightTeam?.player1 ? `${rightTeam.player1.firstname} ${rightTeam.player1.lastname}` : ""}
                      </td>
                      <td>{formatPhoneNumber(rightTeam?.player1?.phone) || ""}</td>
                    </tr>
                    <tr>
                      <td>
                        {leftTeam?.player2 ? `${leftTeam.player2.firstname} ${leftTeam.player2.lastname}` : ""}
                      </td>
                      <td>{formatPhoneNumber(leftTeam?.player2?.phone) || ""}</td>
                      <td>
                        {rightTeam?.player2 ? `${rightTeam.player2.firstname} ${rightTeam.player2.lastname}` : ""}
                      </td>
                      <td>{formatPhoneNumber(rightTeam?.player2?.phone) || ""}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      ))}
    </div>
  );
}
